"use client";

import { cookieDomainForHostname } from "./consent/consent.config";

const FIRST_TOUCH_COOKIE_NAME = "ax_ft";
const FIRST_TOUCH_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

export interface FirstTouch {
  cid: string;
  landing_page: string;
  referrer: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  first_seen_at: string;
}

function readCookieValue(): string | null {
  if (typeof document === "undefined") return null;

  const prefix = `${FIRST_TOUCH_COOKIE_NAME}=`;
  const cookie = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  return cookie ? cookie.slice(prefix.length) : null;
}

function isNullableString(value: unknown): value is string | null {
  return typeof value === "string" || value === null;
}

function parseFirstTouch(value: string): FirstTouch | null {
  try {
    const parsed = JSON.parse(decodeURIComponent(value)) as Partial<FirstTouch>;

    if (
      typeof parsed.cid !== "string" ||
      typeof parsed.landing_page !== "string" ||
      !isNullableString(parsed.referrer) ||
      !UTM_KEYS.every((key) => isNullableString(parsed[key])) ||
      typeof parsed.first_seen_at !== "string"
    ) {
      return null;
    }

    return parsed as FirstTouch;
  } catch {
    return null;
  }
}

function pageWithoutQuery(url: URL): string {
  return `${url.origin}${url.pathname}`;
}

function readReferrer(): string | null {
  if (!document.referrer) return null;

  try {
    return pageWithoutQuery(new URL(document.referrer));
  } catch {
    return null;
  }
}

function cookieDomain(): string | null {
  const configuredDomain = process.env.NEXT_PUBLIC_C15T_COOKIE_DOMAIN?.trim();
  const matchingConfiguredDomain = cookieDomainForHostname(
    configuredDomain,
    window.location.hostname,
  );
  if (matchingConfiguredDomain) return matchingConfiguredDomain;

  const rootDomain = process.env.NEXT_PUBLIC_ROOT_DOMAIN?.trim();
  const sharedDomains = rootDomain ? [rootDomain, "514.ax", "514.ai"] : ["514.ax", "514.ai"];

  for (const domain of sharedDomains) {
    if (window.location.hostname === domain || window.location.hostname.endsWith(`.${domain}`)) {
      return `.${domain}`;
    }
  }

  return null;
}

export function readFirstTouchCookie(): FirstTouch | null {
  const value = readCookieValue();
  return value === null ? null : parseFirstTouch(value);
}

export function ensureFirstTouchCookie(hasTelemetryConsent: boolean): FirstTouch | null {
  if (!hasTelemetryConsent || typeof document === "undefined" || typeof window === "undefined") {
    return null;
  }

  const existingValue = readCookieValue();
  if (existingValue !== null) {
    const existing = parseFirstTouch(existingValue);
    // A malformed cookie would otherwise block attribution until Max-Age
    // expires, so fall through and replace it with a fresh first touch.
    if (existing !== null) return existing;
  }

  const currentUrl = new URL(window.location.href);
  const firstTouch: FirstTouch = {
    cid: crypto.randomUUID(),
    landing_page: pageWithoutQuery(currentUrl),
    referrer: readReferrer(),
    utm_source: currentUrl.searchParams.get("utm_source"),
    utm_medium: currentUrl.searchParams.get("utm_medium"),
    utm_campaign: currentUrl.searchParams.get("utm_campaign"),
    utm_term: currentUrl.searchParams.get("utm_term"),
    utm_content: currentUrl.searchParams.get("utm_content"),
    first_seen_at: new Date().toISOString(),
  };

  const attributes = [
    `${FIRST_TOUCH_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(firstTouch))}`,
    "Path=/",
    `Max-Age=${FIRST_TOUCH_COOKIE_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
  ];
  const domain = cookieDomain();
  if (domain) attributes.push(`Domain=${domain}`);
  if (window.location.protocol === "https:") attributes.push("Secure");

  document.cookie = attributes.join("; ");
  return firstTouch;
}
